import type { EnterpriseRiskDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { EnterpriseRiskRegisterResponseRow } from '@risksmart-app/drizzle/src/types';
import type {
  Enterprise_Risk_Bool_Exp,
  GetEnterpriseRisksFlatQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetEnterpriseRisksFlatDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { ENTERPRISE_RISK_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useGraphqlVariables = (args: UseGetEnterpriseRisksRegisterArgs) => {
  // PASS THE HOOK'S OWN ARGS OBJECT as `filters`, never a rebuilt literal:
  // `buildDashboardWhere`'s `BuilderFor` guard infers from it, and a literal
  // relaxes the required builders.
  const { where } = buildDashboardWhere({
    base: useEntityWhereFilter<Enterprise_Risk_Bool_Exp>(
      Parent_Type_Enum.EnterpriseRisk
    ),
    filters: args,
    datePredicates: ENTERPRISE_RISK_DATE_PREDICATES,
  });

  return { where };
};

/**
 * Enterprise risk's `dashboardFilterConfig` declares no tag, department or
 * ownership filter, so the dashboard half is dates only (COI-878).
 */
export type UseGetEnterpriseRisksRegisterArgs = Pick<
  DashboardFilterArgs<EnterpriseRiskDashboardDateField>,
  'dateRange'
>;

export const useGetEnterpriseRisksRegister = createQueryHook<
  UseGetEnterpriseRisksRegisterArgs,
  EnterpriseRiskRegisterResponseRow[],
  GetEnterpriseRisksFlatQuery
>({
  // `toTrpcDashboardFilterInput` returns `undefined` when every filter is
  // unset, so the two register pages keep the same react-query key they had
  // when this procedure took no input at all.
  trpcQueryOptions: (trpc, { dateRange }) =>
    trpc.frontend.enterpriseRisk.register.queryOptions(
      toTrpcDashboardFilterInput({ dateRange })
    ),
  mapTrpcDataToGraphQL: (data) => ({ enterprise_risk: data }),
  graphqlDocument: GetEnterpriseRisksFlatDocument,
  graphqlVariables: useGraphqlVariables,
});

/**
 * Maps TRPC enterprise risks data to match the GraphQL query structure
 * If Possible Use the hook directly instead.
 */
export function mapTrpcEnterpriseRisksToGraphQL(
  trpcData: EnterpriseRiskRegisterResponseRow[] | undefined
): StrictMappedGraphQLSelection<GetEnterpriseRisksFlatQuery> | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    enterprise_risk: trpcData,
  };
}
